import { useState } from 'react';
import { toast } from 'react-toastify';
import { generateActivationLink, isApiError } from 'src/services/client';

export interface ActivationLinkData {
  activationLink: string;
  expiresOn: string;
}

export interface ActivationLinkResponse {
  message: string;
  data: ActivationLinkData | null;
}

export interface ActivationLinkGeneratorViewModel {
  activationLink: string | null;
  expiresOn: string | null;
  isGenerating: boolean;
  isRevealed: boolean;
  error: string | null;
  generate: () => void;
  copyLink: () => void;
}

const GENERIC_ERROR: string =
  'Could not generate an activation link. Please try again.';

export const useActivationLinkGenerator = (
  userId: string
): ActivationLinkGeneratorViewModel => {
  const [activationLink, setActivationLink] = useState<string | null>(null);
  const [expiresOn, setExpiresOn] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const requestActivationLink = async (): Promise<void> => {
    if (!userId) {
      console.warn('useActivationLinkGenerator: called without a userId');
      return;
    }

    setIsGenerating(true);
    setError(null);

    const response = await generateActivationLink(userId);

    if (isApiError(response)) {
      setError(GENERIC_ERROR);
      setIsGenerating(false);
      return;
    }

    // legacy/workaround: apiReq returns a plain shaped object (not a Response)
    // for 400s, e.g. "...only be created for a contact that has not activated
    // yet." Surface that server message instead of trying to parse JSON off it.
    if (!(response instanceof Response)) {
      const rejection = response as { message?: string };
      setError(rejection.message ?? GENERIC_ERROR);
      setIsGenerating(false);
      return;
    }

    const body: ActivationLinkResponse = await response.json();

    const link: string | undefined = body?.data?.activationLink;

    if (!link) {
      setError(GENERIC_ERROR);
      setIsGenerating(false);
      return;
    }

    setActivationLink(link);
    setExpiresOn(body.data?.expiresOn ?? null);
    setIsGenerating(false);
  };

  const generate = () => {
    void requestActivationLink();
  };

  const copyLink = () => {
    if (!activationLink) {
      console.warn('useActivationLinkGenerator: copy called before a link exists');
      return;
    }

    void navigator.clipboard.writeText(activationLink);

    toast.success('Activation link copied to clipboard.', {
      autoClose: 3500,
      position: 'top-center',
    });
  };

  return {
    activationLink,
    expiresOn,
    isGenerating,
    isRevealed: activationLink !== null,
    error,
    generate,
    copyLink,
  };
};